import {
  trackTauriFailedToRelaunch,
  trackTauriRelaunch,
  trackTauriUpdate,
} from '@/lib/analytics/scribe/tauri';
import { store } from '@/store';
import { systemController } from '@/tauri/controller/system';
import { UpdaterBridgeError } from '@/tauri/updater';
import {
  checkUpdateForFeed,
  installUpdateForFeed,
  resolveUpdaterFeed,
  type UpdaterFeed,
} from '@/tauri/updater/feed';
import { updateFailureMessages } from '@/tauri/updater/updater.messages';
import { isTauri } from '@/tauri/utils';
import { isConfirmedDesktopOfflineNetworkMode } from '@/utils/http/desktopOfflineNetwork';
import { formatMessageOutsideReact } from '@/utils/i18n/intl-bridge';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { createModel } from '@rematch/core';
import { getVersion } from '@tauri-apps/api/app';
import { relaunch } from '@tauri-apps/plugin-process';
import { type MessageDescriptor } from 'react-intl';
import { type RootModel } from '.';
// import { RecordingStatus } from '../scribeModel';

/** Undefined for the global-updater path, which throws the plugin's own uncoded errors. */
const updaterErrorCode = (error: unknown): string | undefined =>
  error instanceof UpdaterBridgeError ? error.errorCode : undefined;

/**
 * Only codes with a remedy the *user* owns get bespoke copy — they can unblock a
 * network or get the app installed by an admin. Everything else (bad signature,
 * malformed manifest, missing platform asset) is ours to fix and unactionable by
 * them, so it gets generic retry copy instead of jargon.
 */
const installFailureBody = (errorCode: string | undefined): MessageDescriptor => {
  switch (errorCode) {
    // A Rust-side deadline surfaces as a reqwest failure, i.e. UPDATER_NETWORK —
    // there is no separate frontend timeout code to handle.
    case 'UPDATER_NETWORK':
      return updateFailureMessages.installNetworkFailed;
    case 'UPDATER_INSTALL_PERMISSION_DENIED':
      return updateFailureMessages.installPermissionDenied;
    default:
      return updateFailureMessages.installFailed;
  }
};

// todo use tanstack's states

export enum UploadStatus {
  Queued = 'queued',
  Uploading = 'uploading',
  Completed = 'completed',
  Failed = 'failed',
}

export type InitialState = {
  current_version?: string;
  cpu_arch?: string;
  version_to_update?: {
    version: string;
    body?: string;
    isInstalled: boolean;
    popup_dismissed: boolean;
    // C4: the feed the check resolved. Install reuses it instead of re-reading
    // the gate, so a flag flip between check and install can't reroute a UK/EU
    // client that checked the certified channel onto the global feed.
    feed: UpdaterFeed;
  };
};

const initialState: InitialState = {};

export const tauriUpdateModel = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setCurrentVersion(state, payload: string) {
      return { ...state, current_version: payload };
    },
    setCpuArch(state, payload?: string) {
      return { ...state, cpu_arch: payload };
    },
    setVersionToUpdate(state, payload: InitialState['version_to_update']) {
      return { ...state, version_to_update: payload };
    },
    clear() {
      return initialState;
    },
  },
  effects: (dispatch) => ({
    async getCurrentVersion() {
      if (!isTauri()) {
        return;
      }
      await getVersion().then((version) => {
        dispatch.tauriUpdateModel.setCurrentVersion(version);
      });
    },
    async getCpuArchOnce() {
      if (!isTauri()) {
        return;
      }
      if (store.getState().tauriUpdateModel.cpu_arch) return;
      try {
        const arch = await systemController.get_cpu_architecture();
        dispatch.tauriUpdateModel.setCpuArch(arch);
      } catch (error) {
        logger.error('Error fetching CPU arch:', error);
        dispatch.tauriUpdateModel.setCpuArch(undefined);
      }
    },
    /**
     * `userInitiated` distinguishes the "Check for updates" button from the
     * background check that fires on mount and on session change. Only the
     * former may raise a toast — surfacing a background failure would interrupt
     * a clinician mid-consult over something they never asked for.
     */
    async handleCheckTauriUpdate(payload?: { userInitiated?: boolean }) {
      const userInitiated = payload?.userInitiated ?? false;
      try {
        if (!isTauri()) {
          return;
        }

        if (isConfirmedDesktopOfflineNetworkMode()) {
          logger.warn('[updater] offline - skipping update check');
          return;
        }

        // The E2E harness drives a pinned build; an "Install & relaunch" toast
        // mid-run only overlays the UI under test.
        if (
          !userInitiated &&
          typeof window !== 'undefined' &&
          window.__IS_E2E__ === true
        ) {
          logger.info('[updater] e2e run - skipping background update check');
          return;
        }

        // The fail-closed routing decision lives in `resolveUpdaterFeed`
        // (APP-6113 §5.19, DC3) — see `src/tauri/updater/feed.ts`.
        const feed = resolveUpdaterFeed();
        if (feed.kind === 'blocked') {
          // Fail-closed: the flag provider hasn't resolved, or the gate is ON
          // but the region is unresolved. Do NO update activity, and clear any
          // stale offer (e.g. from an earlier OFF/global check) so a UK/EU user
          // is never left being shown a stale — possibly global/uncertified —
          // version. Re-runs once the provider/region resolves (the popover
          // effect re-fires).
          logger.warn('[updater] gate/region unresolved — skipping update check');
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
          return;
        }

        const update = await checkUpdateForFeed(feed);
        const versionToUpdate = store.getState().tauriUpdateModel.version_to_update;
        if (update) {
          if (versionToUpdate?.version === update.version) {
            return;
          }
          dispatch.tauriUpdateModel.setVersionToUpdate({
            version: update.version,
            body: update.body,
            popup_dismissed: false,
            isInstalled: false,
            feed, // C4: thread the resolved feed through to install
          });
        } else {
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
        }
      } catch (error) {
        const errorCode = updaterErrorCode(error);
        logger.error('[updater] update check failed', {
          tag: 'updater_check_failed',
          errorCode,
          userInitiated,
          // String, not the raw object: the global-updater path throws the
          // plugin's own errors, which serialize with nested properties and
          // stack traces and would make this log's schema inconsistent.
          error: String(error),
        });
        if (userInitiated) {
          toast({
            tone: 'destructive',
            title: formatMessageOutsideReact(updateFailureMessages.checkFailedTitle),
            description: formatMessageOutsideReact(updateFailureMessages.checkFailed),
          });
        }
      }
    },
    async handleInstallTauriUpdate() {
      if (!isTauri()) {
        return;
      }
      const newUpdate = store.getState().tauriUpdateModel.version_to_update;
      if (!newUpdate) return;

      try {
        // C4: install via the SAME feed the check resolved (threaded on
        // `version_to_update`). Do NOT re-read the gate — a flag flip between
        // check and install must not reroute a UK/EU client that checked the
        // certified channel onto the global feed.
        const installed = await installUpdateForFeed(newUpdate.feed);

        if (installed) {
          dispatch.tauriUpdateModel.setVersionToUpdate({
            ...newUpdate,
            isInstalled: true,
          });
          trackTauriUpdate(newUpdate.version);
          await dispatch.tauriUpdateModel.handleRelaunchApp();
        } else {
          // Not a failure: the feed no longer offers this version (it was
          // pulled, or another install already landed it). Clearing the offer is
          // correct, but it is indistinguishable on screen from a failed
          // install, so log it under its own signature rather than leaving the
          // banner to vanish unexplained.
          logger.warn('[updater] install found no update to apply', {
            tag: 'updater_install_no_update',
            version: newUpdate.version,
          });
          dispatch.tauriUpdateModel.setVersionToUpdate(undefined);
        }
      } catch (error) {
        const errorCode = updaterErrorCode(error);
        logger.error('[updater] install failed', {
          tag: 'updater_install_failed',
          errorCode,
          version: newUpdate.version,
          error: String(error),
        });
        // Deliberately do NOT clear `version_to_update`. A failed install used
        // to drop the offer along with the spinner, leaving no trace that
        // anything had been attempted and no way back to the button — users were
        // reduced to downloading and reinstalling the app by hand. Keeping the
        // offer makes the failure recoverable in place.
        toast({
          tone: 'destructive',
          title: formatMessageOutsideReact(updateFailureMessages.installFailedTitle),
          description: formatMessageOutsideReact(installFailureBody(errorCode)),
        });
      }
    },

    async handleRelaunchApp() {
      if (!isTauri()) {
        return;
      }
      try {
        trackTauriRelaunch();
        await relaunch();
      } catch (error) {
        trackTauriFailedToRelaunch(String(error));
        logger.error('Failed to relaunch app:', error);
      } finally {
        const newUpdate = store.getState().tauriUpdateModel.version_to_update;
        if (newUpdate) {
          dispatch.tauriUpdateModel.setVersionToUpdate({
            ...newUpdate,
            popup_dismissed: true,
          });
        }
      }
    },
  }),
});
